'use client';

import type { CurrentUserResponse } from '@sweetistics/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Globe } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import React, { memo, type ReactNode, useCallback, useMemo } from 'react';
import { useLinkPreviewChat } from '@/components/shared/link-preview/use-link-chat';
import { SummarizerOptionsDialog } from '@/components/summarizer/SummarizerOptionsDialog';
import { SummarizerPanel } from '@/components/summarizer/SummarizerPanel';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ModelSelector } from '@/components/ui/ModelSelector';
import { Slider } from '@/components/ui/slider';
import { useAuth } from '@/hooks/useAuth';
import { useBetaFeatures } from '@/lib/hooks/api/settings-hooks';
import { isUrlExcludedFromLinkPreview } from '@/lib/link-preview/exclusions';
import {
  DEFAULT_SUMMARY_LENGTH_INDEX,
  getSummaryLengthOptionByIndex,
  resolveModelOptionFromCanonical,
  SUMMARY_LENGTH_OPTIONS,
} from '@/lib/link-preview/summary-options';
import { recordLinkClick } from '@/lib/tracking/link-click';
import { type EmbeddableStatus, normalizeEmbeddableStatus } from '@/lib/url-metadata/embeddability';
import { cn } from '@/lib/utils';
import { formatCharacterCountHint } from './character-hint';
import { LinkPreviewErrorState } from './ErrorState';
import { ensureEmbeddabilityClient, useEnsureEmbeddabilityMutation } from './embeddability-client';
import { renderExcludedLink, renderIframeView, renderSkippedPreview, renderTwitterImageCard } from './guards';
import { useLinkPreviewData } from './hooks/useLinkPreviewData';
import { useLinkPreviewViewState } from './hooks/useLinkPreviewViewState';
import { ImagePreview } from './ImagePreview';
import { MetadataPreview } from './MetadataPreview';
import { LinkPreviewSkeleton } from './Skeleton';
import type { LinkMetadata as LinkMetadataType } from './types';
import { useLinkSummary } from './use-link-summary';
import { computeFavicon, deriveDomain, formatDisplayUrl, getSummarizerTitle, isTwitterImageUrl } from './utils';

type LinkMetadata = LinkMetadataType;

interface LinkPreviewProperties {
  children?: ReactNode | undefined;
  className?: string | undefined;
  defaultView?: 'preview' | 'iframe' | undefined;
  metadata?: LinkMetadata | undefined;
  url: string;
}

export function LinkPreview({
  url,
  children,
  className = '',
  metadata: propertiesMetadata,
  defaultView = 'preview',
}: LinkPreviewProperties): ReactNode {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const { mutateAsync: runEnsureEmbeddability } = useEnsureEmbeddabilityMutation();
  const cachedAccount = queryClient.getQueryData<CurrentUserResponse>(['account']);
  const isAdminUser = Boolean(session?.user?.role === 'admin' || cachedAccount?.isAdmin);
  const { data: betaFeatures } = useBetaFeatures({ enabled: Boolean(session?.user?.id) });
  const powerUserEnabled = isAdminUser || Boolean(betaFeatures?.powerUser);
  const allowAdvancedControls = powerUserEnabled;

  const isExcluded = isUrlExcludedFromLinkPreview(url);
  const previewData = useLinkPreviewData({ url, metadata: propertiesMetadata, isExcluded });
  const {
    metadata,
    isLoading,
    isError,
    showPreview,
    resolvedIsTwitterImage,
    resolvedImageIsTwitterImage,
    resolvedIsTweetUrl,
  } = previewData;

  const readEmbeddableStatus = useCallback((source: LinkMetadata | null | undefined): EmbeddableStatus | null => {
    const rawStatus = source?.embeddableStatus;
    return typeof rawStatus === 'string' ? normalizeEmbeddableStatus(rawStatus) : null;
  }, []);

  const metadataEmbeddable = readEmbeddableStatus(metadata);
  const propertyEmbeddable = readEmbeddableStatus(propertiesMetadata);
  const resolvedEmbeddableStatus = metadataEmbeddable ?? propertyEmbeddable ?? 'unknown';

  const metadataUrl = typeof metadata?.url === 'string' ? metadata.url : null;
  const metadataTitle = typeof metadata?.title === 'string' ? metadata.title : null;
  const metadataImage = typeof metadata?.image === 'string' ? metadata.image : null;
  const metadataDescription = typeof metadata?.description === 'string' ? metadata.description : null;
  const metadataFavicon = typeof metadata?.favicon === 'string' ? metadata.favicon : null;

  const {
    viewMode,
    imageError,
    iframeUrl,
    iframeError,
    isIframeLoading,
    isFullscreen,
    currentIframeTitle,
    embedSupport,
    markImageError,
    openIframeView,
    closeIframeView,
    handleIframeBack,
    handleIframeLoad,
    handleIframeError,
    toggleFullscreen,
    triggerProbeIfNeeded,
    applyEmbedStatus,
  } = useLinkPreviewViewState({
    url,
    defaultView,
    isExcluded,
    initialEmbeddableStatus: resolvedEmbeddableStatus,
  });

  const {
    summaryState,
    summaryDisplay,
    isSummarizing,
    settingsOpen,
    setSettingsOpen,
    handleSummarizeButtonClick,
    handleSettingsSummarize,
    draftSummaryLengthIndex,
    setDraftSummaryLengthIndex,
    setDraftModel,
    canonicalDraftModel,
    summaryDetailsCopy,
    shouldShowSummarizeControls,
    isSummaryDismissed,
    dismissSummary,
  } = useLinkSummary({ url, powerUserEnabled });

  const activeSummaryLengthOption = getSummaryLengthOptionByIndex(draftSummaryLengthIndex);
  const defaultSummaryLengthOption = getSummaryLengthOptionByIndex(DEFAULT_SUMMARY_LENGTH_INDEX);
  const summaryLengthDescription =
    activeSummaryLengthOption?.description ?? defaultSummaryLengthOption?.description ?? '';

  const ensureEmbeddability = useCallback(
    async (force = false): Promise<EmbeddableStatus> => {
      if (!force) {
        if (embedSupport === 'allowed') {
          return 'allow';
        }
        if (embedSupport === 'blocked') {
          return 'deny';
        }
        if (resolvedEmbeddableStatus !== 'unknown') {
          return resolvedEmbeddableStatus;
        }
      }
      const status = await ensureEmbeddabilityClient({
        url,
        queryClient,
        forceRefresh: force,
        ensureEmbeddability: runEnsureEmbeddability,
      });
      if (status !== 'unknown') {
        applyEmbedStatus(status);
      }
      return status;
    },
    [applyEmbedStatus, embedSupport, queryClient, resolvedEmbeddableStatus, runEnsureEmbeddability, url]
  );

  const { isChatEnabled, isChatLoading, startChatAction } = useLinkPreviewChat({
    url,
    metadata,
    summaryDisplay,
    summaryState,
  });

  const handleChat = useCallback(
    (event?: React.MouseEvent<HTMLButtonElement>) => {
      startChatAction('chat', event).catch((error: unknown) => {
        console.error('Failed to start chat from link preview', error);
      });
    },
    [startChatAction]
  );

  const handleExplain = useCallback(() => {
    startChatAction('explain').catch((error: unknown) => {
      console.error('Failed to explain link preview', error);
    });
  }, [startChatAction]);

  const handleFactCheck = useCallback(() => {
    startChatAction('fact-check').catch((error: unknown) => {
      console.error('Failed to fact-check link preview', error);
    });
  }, [startChatAction]);

  const handleChatButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setSettingsOpen(false);
      handleChat(event);
    },
    [handleChat, setSettingsOpen]
  );

  const handleProbe = useCallback(() => {
    const runProbe = async () => {
      if (embedSupport === 'allowed') {
        triggerProbeIfNeeded();
        return;
      }
      if (embedSupport === 'blocked') {
        return;
      }
      const status = await ensureEmbeddability();
      if (status === 'allow' || status === 'unknown') {
        triggerProbeIfNeeded();
      }
    };

    runProbe().catch((error: unknown) => {
      console.error('Failed to probe link preview', error);
    });
  }, [embedSupport, ensureEmbeddability, triggerProbeIfNeeded]);

  const handleOpenIframeButton = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const run = async () => {
        if (embedSupport === 'allowed') {
          openIframeView();
          return;
        }
        if (embedSupport === 'blocked') {
          return;
        }
        const status = await ensureEmbeddability();
        if (status === 'allow') {
          openIframeView();
        } else if (status === 'unknown') {
          triggerProbeIfNeeded();
        }
      };

      run().catch((error: unknown) => {
        console.error('Failed to open iframe preview', error);
      });
    },
    [embedSupport, ensureEmbeddability, openIframeView, triggerProbeIfNeeded]
  );

  const loading = isLoading;
  const hasError = isError && !metadata;

  const summarizerTitle = useMemo(() => getSummarizerTitle(url, resolvedIsTweetUrl), [resolvedIsTweetUrl, url]);

  const shouldRenderSummaryCard = summaryDisplay.shouldRenderSummaryCard && !isSummaryDismissed;

  const summaryCharacterHint = useMemo(() => {
    const summaryMetadata = summaryState.metadata;
    const resolvedCount =
      summaryMetadata.summaryCharacters ??
      summaryMetadata.sourceCharacters ??
      summaryMetadata.transcriptCharacters ??
      null;
    return formatCharacterCountHint(resolvedCount);
  }, [summaryState.metadata]);

  const generatingSummaryLabel = summaryCharacterHint
    ? `Generating summary (~${summaryCharacterHint} characters)…`
    : 'Generating summary…';

  const handleSummaryDismiss = useCallback(() => {
    dismissSummary();
  }, [dismissSummary]);

  const excludedNode = renderExcludedLink({ isExcluded, url, className, children });
  if (excludedNode) {
    return excludedNode;
  }

  if (loading) {
    return (
      <div className={className}>
        {children}
        <LinkPreviewSkeleton />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={className}>
        {children}
        <LinkPreviewErrorState
          url={url}
          onRetry={() => {
            queryClient.invalidateQueries({ queryKey: ['url-metadata', url] }).catch((error: unknown) => {
              console.error('Failed to invalidate URL metadata cache', error);
            });
          }}
        />
      </div>
    );
  }

  const skippedNode = renderSkippedPreview({
    showPreview,
    hasError,
    loading,
    metadata,
    url,
    children,
    isTwitterImage: isTwitterImageUrl(url),
    resolvedImageIsTwitterImage,
    resolvedIsTweetUrl,
  });
  if (skippedNode !== undefined) {
    return <div className={className}>{skippedNode}</div>;
  }

  const twitterImageNode = renderTwitterImageCard({
    resolvedIsTwitterImage,
    metadata,
    url,
    className,
    children,
  });
  if (twitterImageNode) {
    return twitterImageNode;
  }

  const domain = deriveDomain(url);
  const faviconUrl = metadataFavicon && metadataFavicon.length > 0 ? metadataFavicon : computeFavicon(url);
  const displayUrl = metadataUrl ? formatDisplayUrl(metadataUrl) : formatDisplayUrl(url);

  const iframeNode = renderIframeView({
    viewMode,
    className,
    isFullscreen,
    children,
    url,
    iframeUrl,
    isIframeLoading,
    iframeError,
    currentIframeTitle,
    faviconUrl,
    domain,
    onBack: handleIframeBack,
    onClose: closeIframeView,
    onToggleFullscreen: toggleFullscreen,
    onOpenInNewTab: () => {
      window.open(iframeUrl || url, '_blank', 'noopener,noreferrer');
    },
    onIframeLoad: (title) => {
      handleIframeLoad(title ?? metadataTitle ?? undefined);
    },
    onIframeError: handleIframeError,
    metadataTitle: metadataTitle ?? undefined,
  });
  if (iframeNode) {
    return iframeNode;
  }

  const canOpenIframe = embedSupport === 'allowed';

  return (
    <div className={className}>
      {children}

      <div className="mt-3 relative">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="group/link-preview peer block"
          data-testid="link-preview-anchor"
          onMouseEnter={handleProbe}
          onClick={(event) => {
            event.stopPropagation();
            recordLinkClick({ url });
          }}
        >
          <div className="relative border border-border/60 rounded-2xl overflow-hidden transition-all duration-200 hover:shadow-2xl hover:border-primary/40 dark:hover:border-primary/60 bg-card/95 backdrop-blur-xl shadow-lg">
            <ImagePreview
              imageUrl={imageError ? null : metadataImage}
              title={metadataTitle ?? undefined}
              onError={markImageError}
              showFallback={Boolean((!metadataImage || imageError) && (metadataTitle || metadataDescription))}
            />

            <MetadataPreview
              metadata={metadata ?? null}
              domain={domain}
              faviconUrl={faviconUrl}
              displayUrl={displayUrl}
              shouldShowSummarizeControls={shouldShowSummarizeControls}
              summaryButtonLabel={summaryDisplay.summarizeButtonLabel}
              onSummarize={handleSummarizeButtonClick}
              isSummarizing={isSummarizing}
              isChatEnabled={isChatEnabled}
              isChatLoading={isChatLoading}
              onChat={handleChat}
              onExplain={handleExplain}
              onFactCheck={handleFactCheck}
              allowAdvancedControls={allowAdvancedControls}
              onOpenSettingsMenu={() => {
                setSettingsOpen(true);
              }}
            />

            <div className="absolute inset-0 bg-gradient-to-tr from-primary/[0.03] via-transparent to-primary/[0.03] opacity-0 group-hover/link-preview:opacity-100 transition-all duration-200 pointer-events-none" />
            <div className="absolute -inset-px bg-gradient-to-r from-primary/10 via-transparent to-primary/10 rounded-2xl opacity-0 group-hover/link-preview:opacity-100 blur-xl transition-all duration-300 z-layer-below" />
          </div>
        </a>

        {canOpenIframe ? (
          <Button
            variant="outline"
            size="sm"
            className="absolute -top-2 right-2 z-layer-surface opacity-0 transition-opacity gap-2 peer-hover:opacity-100 peer-focus-visible:opacity-100 hover:opacity-100 focus-visible:opacity-100"
            onClick={handleOpenIframeButton}
          >
            <Globe className="h-3 w-3" aria-hidden="true" />
            Preview in iframe
          </Button>
        ) : null}
      </div>

      <AnimatePresence initial={false}>
        {shouldRenderSummaryCard ? (
          <motion.div
            key="link-summary-card"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-3"
          >
            <SummarizerPanel
              cardProps={{
                status: summaryState.status,
                text: summaryState.text,
                error: summaryState.error,
                markdown: true,
                generatingLabel: generatingSummaryLabel,
                emptyLabel: 'No summary available for this link.',
                onChat: isChatEnabled ? handleChat : undefined,
                isChatEnabled,
                isChatLoading,
                chatLabel: 'Chat',
                chatLoadingLabel: 'Chatting…',
                helpContent: summaryDetailsCopy,
                onDismiss: handleSummaryDismiss,
                dismissTooltip: 'Hide summary',
                textClassName: 'leading-relaxed space-y-2 text-sm',
              }}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      {allowAdvancedControls ? (
        <SummarizerOptionsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          isSummarizing={isSummarizing}
          title={summarizerTitle}
          description="Adjust the summary length and model before running the link summarizer."
          onCancel={() => {
            setSettingsOpen(false);
          }}
          onSubmit={handleSettingsSummarize}
          onChat={isChatEnabled ? handleChatButtonClick : undefined}
          isChatLoading={isChatLoading}
          isChatDisabled={!isChatEnabled}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="font-medium">Summary length</Label>
              <span className="text-xs text-muted-foreground">{summaryLengthDescription}</span>
            </div>
            <Slider
              value={[draftSummaryLengthIndex]}
              onValueChange={(value) => {
                setDraftSummaryLengthIndex(value[0] ?? draftSummaryLengthIndex);
              }}
              min={0}
              max={SUMMARY_LENGTH_OPTIONS.length - 1}
              step={1}
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              {SUMMARY_LENGTH_OPTIONS.map((option, index) => (
                <span
                  key={option.value}
                  className={cn(index === draftSummaryLengthIndex && 'text-foreground font-medium')}
                >
                  {option.label}
                </span>
              ))}
            </div>
          </div>

          <ModelSelector
            value={canonicalDraftModel}
            onChange={(next) => {
              const alias = resolveModelOptionFromCanonical(next);
              if (alias) {
                setDraftModel(alias);
              }
            }}
            isAdmin
            showIcon={false}
            showLabel
            helperText={null}
            labelText="Model"
            disabled={isSummarizing}
          />
        </SummarizerOptionsDialog>
      ) : null}
    </div>
  );
}

export default memo(LinkPreview);
export type { LinkMetadata };
